import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebaseConfig.js";

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10; // Categorías por página

  // Obtener todas las categorías desde Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      const querySnapshot = await getDocs(collection(db, "categorias"));
      const fetchedCategories = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(fetchedCategories);
    };
    fetchCategories();
  }, []);

  // Agregar una nueva categoría
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) return;

    try {
      const docRef = await addDoc(collection(db, "categorias"), { name: newCategory });
      setCategories([...categories, { id: docRef.id, name: newCategory }]);
      setNewCategory("");
    } catch (error) {
      console.error("Error al agregar la categoría:", error);
    }
  };

  // Actualizar una categoría existente
  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!currentCategory.name.trim()) return;

    try {
      const categoryDoc = doc(db, "categorias", currentCategory.id);
      await updateDoc(categoryDoc, { name: currentCategory.name });

      setCategories(
        categories.map((category) =>
          category.id === currentCategory.id
            ? { ...category, name: currentCategory.name }
            : category
        )
      );
      setIsEditing(false);
      setCurrentCategory(null);
    } catch (error) {
      console.error("Error al actualizar la categoría:", error);
    }
  };

  // Eliminar una categoría
  const handleDeleteCategory = async (id) => {
    try {
      await deleteDoc(doc(db, "categorias", id));
      setCategories(categories.filter((category) => category.id !== id));
    } catch (error) {
      console.error("Error al eliminar la categoría:", error);
    }
  };

  // Cambiar de página
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ padding: "2rem" }}>
      <Typography variant="h4" gutterBottom>
        Gestión de Categorías
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        Crea, edita y elimina categorías según las necesidades del sitio.
      </Typography>

      {/* Formulario */}
      <Paper sx={{ padding: "1rem", marginBottom: "2rem" }} elevation={3}>
        <form onSubmit={isEditing ? handleUpdateCategory : handleAddCategory}>
          <TextField
            label="Nombre de la categoría"
            variant="outlined"
            value={isEditing ? currentCategory?.name : newCategory}
            onChange={(e) =>
              isEditing
                ? setCurrentCategory({ ...currentCategory, name: e.target.value })
                : setNewCategory(e.target.value)
            }
            fullWidth
            sx={{ marginBottom: "1rem" }}
          />
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Button type="submit" variant="contained" color="primary">
              {isEditing ? "Actualizar" : "Agregar"}
            </Button>
            {isEditing && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setCurrentCategory(null);
                }}
              >
                Cancelar
              </Button>
            )}
          </Box>
        </form>
      </Paper>

      {/* Tabla de categorías */}
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Categoría</strong></TableCell>
              <TableCell align="right"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setIsEditing(true);
                        setCurrentCategory(category);
                      }}
                      sx={{ marginRight: "0.5rem" }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      Eliminar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={categories.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[]}
        />
      </TableContainer>
    </Box>
  );
};

export default Categories;
